import { Controller } from "@hotwired/stimulus";

function formatTime(timeString) {
  const [hour, minute] = timeString.split(":");
  const d = new Date();
  d.setHours(hour, minute, 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default class extends Controller {
  static targets = [
    "form",
    "serviceField",
    "providerField",
    "dateField",
    "timeField",
    "timeSlots",
    "dateScroller",
    "progressBar",
    "durationDisplay",
    "submitButton",
  ];

  static values = {
    workspaceId: String,
    siteId: String,
    providerColor: String,
  };

  async connect() {
    await this.restoreInitialSelection();
    this.updateProgress();
  }

  async restoreInitialSelection() {
    const providerId = this.providerFieldTarget.value;
    const selectedDate = this.dateFieldTarget.value;
    const selectedTime = this.timeFieldTarget.value;

    if (!providerId) return;

    await this.fetchAvailableDates(providerId);
    if (!selectedDate) return;

    const selectedDateButton = this.dateScrollerTarget.querySelector(
      `[data-date="${CSS.escape(selectedDate)}"]`
    );
    if (selectedDateButton && !selectedDateButton.disabled) {
      selectedDateButton.dataset.selected = "true";
    }

    await this.fetchTimeSlots(providerId, selectedDate);
    if (!selectedTime) return;

    const selectedTimeButton = this.timeSlotsTarget.querySelector(
      `[data-time="${CSS.escape(selectedTime)}"]`
    );
    if (selectedTimeButton) this.updateSelectedTimeSlot(selectedTimeButton);
  }

  async providerChanged(event) {
    const providerId = event.currentTarget.value;
    this.providerFieldTarget.value = providerId;

    this.updateProviderSelection(event);
    await this.fetchAvailableDates(providerId);
    this.updateProgress();
  }

  updateProviderSelection(event) {
    const providerColor = event.currentTarget.dataset.providerColor;
    const allProviders = document.querySelectorAll(
      'input[name="appointment[service_provider_id]"]'
    );

    allProviders.forEach((provider) => {
      const label = provider.nextElementSibling;
      if (!label) return;

      if (provider.checked) {
        label.classList.add("ring-2");
        if (providerColor) {
          label.style.borderColor = providerColor;
          label.style.boxShadow = `0 0 0 2px ${providerColor}33`;
        }
      } else {
        label.classList.remove("ring-2");
        label.style.borderColor = "";
        label.style.boxShadow = "";
      }
    });
  }

  async fetchAvailableDates(providerId) {
    try {
      const resp = await fetch(
        `/workspaces/${this.workspaceIdValue}/sites/${this.siteIdValue}/appointments/providers/${providerId}/available_dates`
      );
      if (!resp.ok) throw new Error("Failed to fetch dates");
      const dates = await resp.json();
      this.updateDateAvailability(dates);
    } catch (err) {
      console.error(err);
    }
  }

  updateDateAvailability(dates) {
    const buttons = this.dateScrollerTarget.querySelectorAll("button");

    buttons.forEach((button) => {
      const dateStr = button.dataset.date;
      const dateData = dates.find((d) => d.date === dateStr);

      button.disabled = !dateData?.available;
      button.dataset.available = dateData?.available ? "true" : "false";

      if (dateData?.available) {
        button.classList.remove("bg-gray-100", "dark:bg-gray-800");
        button.classList.add("bg-white/50", "dark:bg-slate-800/50");
      } else {
        button.classList.remove("bg-white/50", "dark:bg-slate-800/50");
        button.classList.add("bg-gray-100", "dark:bg-gray-800");
      }
    });
  }

  async selectDate(event) {
    const dateStr = event.currentTarget.dataset.date;
    if (!dateStr) return;

    this.dateScrollerTarget
      .querySelectorAll("[data-selected='true']")
      .forEach((button) => delete button.dataset.selected);
    event.currentTarget.dataset.selected = "true";
    this.dateFieldTarget.value = dateStr;
    this.timeFieldTarget.value = "";
    const providerId = this.providerFieldTarget.value;

    await this.fetchTimeSlots(providerId, dateStr);
    this.updateProgress();
  }

  async fetchTimeSlots(providerId, date) {
    try {
      const resp = await fetch(
        `/workspaces/${this.workspaceIdValue}/sites/${this.siteIdValue}/appointments/providers/${providerId}/slots?date=${date}`
      );
      if (!resp.ok) throw new Error("Failed to fetch slots");
      const slots = await resp.json();
      this.renderTimeSlots(slots);
    } catch (err) {
      console.error(err);
      this.timeSlotsTarget.innerHTML = `<p class="text-red-500">Error loading time slots</p>`;
    }
  }

  renderTimeSlots(slots) {
    if (!slots?.length) {
      this.timeSlotsTarget.innerHTML = this.noSlotsTemplate();
      return;
    }

    const availableSlots = slots.filter((slot) => slot.available);
    if (!availableSlots.length) {
      this.timeSlotsTarget.innerHTML = this.allBookedTemplate();
      return;
    }

    this.timeSlotsTarget.innerHTML = `
      <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
        ${this.renderSlotButtons(availableSlots)}
      </div>
    `;
  }

  renderSlotButtons(slots) {
    const color = this.providerColorValue || "#6366F1";

    return slots
      .map(
        (slot) => `
      <button type="button"
              class="relative group px-4 py-3 border rounded-lg text-sm
                     bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm
                     text-slate-700 dark:text-slate-300
                     hover:border-indigo-500/50 hover:shadow-sm
                     transition-all duration-200"
              style="border-color: ${color}33"
              data-action="click->appointment-form#selectTime"
              data-time="${slot.time}">
        <span class="relative z-10 group-hover:text-indigo-600 
                     dark:group-hover:text-indigo-400 
                     transition-colors">
          ${formatTime(slot.time)}
        </span>
      </button>
    `
      )
      .join("");
  }

  noSlotsTemplate() {
    return `
      <div class="text-center py-6">
        <p class="text-gray-700 dark:text-gray-300">
          No available time slots for this date.
        </p>
      </div>
    `;
  }

  allBookedTemplate() {
    return `
      <div class="text-center py-6">
        <p class="text-gray-700 dark:text-gray-300">
          All slots are booked for this date.
        </p>
      </div>
    `;
  }

  selectTime(event) {
    const time = event.currentTarget.dataset.time;
    if (!time) return;

    this.timeFieldTarget.value = time;
    this.updateSelectedTimeSlot(event.currentTarget);
    this.updateProgress();
  }

  updateSelectedTimeSlot(selectedButton) {
    const buttons = this.timeSlotsTarget.querySelectorAll("button");
    buttons.forEach((btn) => {
      btn.classList.remove("ring-2", "ring-indigo-500");
    });
    selectedButton.classList.add("ring-2", "ring-indigo-500");
  }

  updateProgress() {
    const requiredFields = [
      this.serviceFieldTarget,
      this.providerFieldTarget,
      this.dateFieldTarget,
      this.timeFieldTarget,
    ];

    const progress =
      (requiredFields.filter((f) => f.value).length / requiredFields.length) *
      100;
    this.progressBarTarget.style.width = `${progress}%`;
    this.submitButtonTarget.disabled = progress < 100;
  }

  serviceSelected(event) {
    const serviceId = event.currentTarget.value;
    this.serviceFieldTarget.value = serviceId;

    const duration = event.currentTarget.dataset.duration;
    if (duration) {
      this.durationDisplayTarget.textContent = `${duration} minutes`;
    }

    this.updateProgress();
  }
}
